export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://tearabytess.co.uk/familyAPI";

export function getUsernameFromToken(token) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.username || null;
  } catch {
    return null;
  }
}

export function authHeaders(token, extra) {
  return { Authorization: `Bearer ${token}`, ...extra };
}

function jsonHeaders(token) {
  return authHeaders(token, { "Content-Type": "application/json" });
}

async function asJson(res) {
  let body = null;
  try {
    body = await res.json();
  } catch {
    // no body
  }
  if (!res.ok) {
    const err = new Error(body?.error || res.statusText || "Request failed");
    err.status = res.status;
    throw err;
  }
  return body;
}

export async function login(username, password) {
  const res = await fetch(`${API_BASE_URL}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  return asJson(res);
}

export async function listFiles(token, path) {
  const res = await fetch(`${API_BASE_URL}/files?path=${encodeURIComponent(path)}`, {
    headers: authHeaders(token),
  });
  return asJson(res);
}

export async function listAllFolders(token) {
  const res = await fetch(`${API_BASE_URL}/all-folders`, { headers: authHeaders(token) });
  return asJson(res);
}

/** Uploads with progress reporting; onProgress receives a 0-100 number. */
export function uploadFiles(token, relativePath, files, onProgress) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append("relativePath", relativePath);
    files.forEach((f) => fd.append("files", f));

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE_URL}/upload`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(fd);
  });
}

export async function createFolder(token, currentPath, name) {
  const res = await fetch(`${API_BASE_URL}/folders`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ currentPath, name }),
  });
  return asJson(res);
}

export async function moveFile(token, fileName, currentPath, destinationFolder) {
  const res = await fetch(`${API_BASE_URL}/move-simplified`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ fileName, currentPath, destinationFolder }),
  });
  return asJson(res);
}

export async function copyFiles(token, currentPath, files, destination) {
  const res = await fetch(`${API_BASE_URL}/copy`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ currentPath, files, destination }),
  });
  return asJson(res);
}

export async function renameFile(token, currentPath, oldName, newName) {
  const res = await fetch(`${API_BASE_URL}/rename`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ currentPath, oldName, newName }),
  });
  return asJson(res);
}

export async function bulkAction(token, currentPath, action, files, destination) {
  const res = await fetch(`${API_BASE_URL}/bulk`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ currentPath, action, files, destination }),
  });
  return asJson(res);
}

export async function listTrash(token) {
  const res = await fetch(`${API_BASE_URL}/trash`, { headers: authHeaders(token) });
  return asJson(res);
}

export async function restoreTrashItem(token, trashName) {
  const res = await fetch(`${API_BASE_URL}/trash/restore`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ trashName }),
  });
  return asJson(res);
}

export async function deleteTrashItem(token, trashName) {
  const res = await fetch(`${API_BASE_URL}/trash/${encodeURIComponent(trashName)}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return asJson(res);
}

export function viewUrl(filePath, token) {
  return `${API_BASE_URL}/view?filePath=${encodeURIComponent(filePath)}&token=${encodeURIComponent(token)}`;
}

export async function fetchBlobUrl(token, filePath) {
  const res = await fetch(`${API_BASE_URL}/view?filePath=${encodeURIComponent(filePath)}`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error("Failed to load file");
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
